import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { AppButton } from '../../../../Components/FormKit';
import { useCarCalculator } from '../../../../Hooks/useCarCalculator';
import { fetchRecotizacion, mensajeDeError, postRecotizar } from '../../../../services/adminRecotizacionApi';
import { seguroManualDesdeActual, validarSeguroManual } from '../../../../Utils/seguroManual';
import { CarDataForm } from '../../../Cotizador/Steps/Step1/CarDataForm';
import { recotizarDialogCSS } from './BudgetsManagement.css';
import SeguroManualCard from './SeguroManualCard';
import {
    COPY_EXPEDIENTE,
    SEGURO_MANUAL,
    SEGURO_MANUAL_VACIO,
    armarPayloadRecotizacion,
    avisoDeFaltantes,
    budgetParaCalculadora,
    errorDeRecotizacion,
    plazoActual,
    rodacSinSeguro,
    seguroManualAplicado,
    textoBloqueo,
    textoExito,
    tieneExpediente,
} from './recotizarBudget';

/* Recotizar una cotización de admin/usuarios sobre el MISMO budget (D-ADM-2 a D-ADM-5).
 *
 * GET admin/cotizaciones/{id}/recotizacion → formulario del cotizador precargado →
 * POST admin/cotizaciones/{id}/recotizar. El secret nunca llega al front: lo
 * pone el backend. Modelo: dashboard/components/RecotizacionDialog.jsx, sin
 * borradores ni factura. */

/* El formulario. Solo se monta con los datos ya cargados: `data.budget` es la
 * misma referencia todo el tiempo, así el hook lo precarga una sola vez. */
const FormularioRecotizar = ({ budgetId, data, toast, onDone, onHide, onBloqueo }) => {
    const [, setStep] = useState(1);
    const internalVariables = useRef({ secret: null, budget_id: budgetId });

    const {
        calculatorState,
        setState,
        loading,
        plazoVal,
        setPlazoVal,
        plazoTimer,
        buildSubmitPayload,
        errorsArray,
        loadInsurances,
        cleanErrors,
        setClass,
        sumAmountAndAccesories,
        typeRegime,
        residualValue,
        stateAmple,
        percentageDownPayment,
        hadleResidualValue,
        type_coverage,
        handleStatePlateChange,
    } = useCarCalculator(internalVariables, setStep, data.budget, null, true, null, data.budget.budget_id ?? budgetId);

    const [seguroManual, setSeguroManual] = useState(() => seguroManualDesdeActual(data.seguro_actual) || SEGURO_MANUAL_VACIO);
    const [erroresManual, setErroresManual] = useState(null);
    const enviando = useRef(false);

    const mostrar = (msg) => toast?.current?.show({ life: 6000, ...msg });

    const plazo = plazoActual(calculatorState.rodac);
    const aplicado = seguroManualAplicado(calculatorState);
    const mostrarManual = aplicado || rodacSinSeguro(calculatorState);

    // Otro plazo pide otras primas y obliga a volver a aplicar: los errores viejos sobran.
    useEffect(() => { setErroresManual(null); }, [plazo]);

    // Si RODAC vuelve a traer opciones con el manual aplicado, gana RODAC (D-ADM-17).
    const hayOpcionesRodac = (calculatorState.rodac.insurances || []).length > 0;
    useEffect(() => {
        if (aplicado && hayOpcionesRodac) {
            setState((prev) => ({ ...prev, rodac: { ...prev.rodac, insurance: null } }));
        }
    }, [aplicado, hayOpcionesRodac, setState]);

    // canContinue() deja los faltantes en errorsArray hasta el siguiente render:
    // el aviso sale de aquí y no de handleSubmit, que todavía ve la lista vieja.
    const [avisoFaltantes, setAvisoFaltantes] = useState(0);
    const avisado = useRef(0);
    // Faltó el seguro con la tarjeta manual sin usar: se resalta y el toast dice qué pulsar.
    const [pendienteManual, setPendienteManual] = useState(false);
    useEffect(() => {
        if (avisoFaltantes === avisado.current) return;
        avisado.current = avisoFaltantes;
        const faltaUsarManual = mostrarManual && !aplicado && (errorsArray || []).includes('insurance');
        if (faltaUsarManual) setPendienteManual(true);
        toast?.current?.show({ severity: 'warn', life: 6000, ...avisoDeFaltantes(errorsArray, faltaUsarManual) });
    }, [avisoFaltantes, errorsArray, toast, mostrarManual, aplicado]);
    useEffect(() => {
        if (aplicado || !mostrarManual) setPendienteManual(false);
    }, [aplicado, mostrarManual]);

    const cambiarSeguroManual = (nuevo) => {
        setSeguroManual(nuevo);
        // Con errores a la vista, se revalida para que se vayan quitando.
        if (erroresManual) {
            const r = validarSeguroManual({ plazo, ...nuevo });
            setErroresManual(r.ok ? null : r.errores);
        }
    };

    const aplicarSeguroManual = () => {
        const r = validarSeguroManual({ plazo, ...seguroManual });
        setErroresManual(r.ok ? null : r.errores);
        if (!r.ok) return;
        cleanErrors('insurance');
        setState((prev) => ({ ...prev, rodac: { ...prev.rodac, insurance: SEGURO_MANUAL } }));
    };

    const quitarSeguroManual = () => {
        setErroresManual(null);
        setState((prev) => ({ ...prev, rodac: { ...prev.rodac, insurance: null } }));
    };

    const handleSubmit = async () => {
        if (enviando.current) return;

        const built = buildSubmitPayload();
        if (!built) {
            setAvisoFaltantes((n) => n + 1);
            // Al rechazar, CarDataForm lleva la vista al primer campo con error.
            throw new Error('faltan_campos');
        }

        let manual = null;
        if (aplicado) {
            const plazoPayload = built.payload.term;
            const r = validarSeguroManual({ plazo: plazoPayload, ...seguroManual });
            if (!r.ok) {
                setErroresManual(r.errores);
                mostrar({ severity: 'warn', summary: 'Revisa el seguro manual', detail: 'Faltan datos del seguro manual.' });
                return;
            }
            manual = { plazo: plazoPayload, ...seguroManual };
        }

        enviando.current = true;
        try {
            const res = await postRecotizar(budgetId, armarPayloadRecotizacion(built.payload, manual));
            // Los motores pueden contestar 200 con `error` (ver submit() del hook).
            if (res?.error) {
                mostrar({ severity: 'error', summary: 'No se pudo recotizar', detail: String(res.error) });
                return;
            }
            mostrar({ severity: 'success', summary: `Cotización #${budgetId}`, detail: textoExito(res) });
            onDone?.(res);
            onHide?.();
        } catch (err) {
            const e = errorDeRecotizacion(err);
            if (e.campos) setErroresManual(e.campos);
            mostrar({ severity: e.severity, summary: e.summary, detail: e.detail });
            if (e.bloqueo) onBloqueo?.(e.bloqueo);
        } finally {
            enviando.current = false;
        }
    };

    return (
        <CarDataForm
            state={calculatorState}
            setState={setState}
            setStep={setStep}
            loading={loading}
            plazoVal={plazoVal}
            setPlazoVal={setPlazoVal}
            plazoTimer={plazoTimer}
            submit={handleSubmit}
            loadInsurances={loadInsurances}
            cleanErrors={cleanErrors}
            setClass={setClass}
            sumAmountAndAccesories={sumAmountAndAccesories}
            typeRegime={typeRegime}
            residualValue={residualValue}
            stateAmple={stateAmple}
            percentageDownPayment={percentageDownPayment}
            hadleResidualValue={hadleResidualValue}
            type_coverage={type_coverage}
            handleStatePlateChange={handleStatePlateChange}
            isAdmin
            adminMode
            insuranceSlot={mostrarManual ? (
                <SeguroManualCard
                    plazo={plazo}
                    valor={seguroManual}
                    onChange={cambiarSeguroManual}
                    errores={erroresManual}
                    aplicado={aplicado}
                    pendiente={pendienteManual}
                    onAplicar={aplicarSeguroManual}
                    onQuitar={quitarSeguroManual}
                />
            ) : null}
        />
    );
};

/* Carga, bloqueo o formulario. Vive dentro del Dialog, que desmonta a sus hijos
 * al cerrarse: cada vez que se abre arranca limpio y vuelve a pedir el GET. */
const RecotizarContenido = ({ budgetId, toast, onDone, onHide }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [intento, setIntento] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setData(null);
        setError(null);
        fetchRecotizacion(budgetId, { signal: controller.signal })
            .then((res) => setData({ ...res, budget: budgetParaCalculadora(res?.budget) }))
            .catch((err) => {
                if (!controller.signal.aborted) setError(mensajeDeError(err, 'No se pudo cargar la cotización.'));
            });
        return () => controller.abort();
    }, [budgetId, intento]);

    // Un 409 al guardar (p. ej. se emitió la póliza mientras tanto) cambia a la vista de bloqueo.
    const bloquear = (bloqueo) => setData((prev) => (prev ? { ...prev, bloqueo } : prev));

    const cerrar = <AppButton label="Cerrar" variant="secondary" size="sm" onClick={onHide} />;

    if (error) {
        return (
            <div className="rbd-estado rbd-estado--error">
                <i className="pi pi-exclamation-triangle" />
                <p>{error}</p>
                <div className="rbd-acciones">
                    <AppButton label="Reintentar" icon="pi pi-refresh" variant="info" size="sm" onClick={() => setIntento((n) => n + 1)} />
                    {cerrar}
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="rbd-estado">
                <i className="pi pi-spin pi-spinner" />
                <span>Cargando cotización…</span>
            </div>
        );
    }

    if (data.bloqueo || !data.budget) {
        return (
            <div className="rbd-estado rbd-estado--bloqueo">
                <i className="pi pi-lock" />
                <p>{data.bloqueo ? textoBloqueo(data.bloqueo) : 'La cotización no trae datos para recotizar.'}</p>
                <div className="rbd-acciones">{cerrar}</div>
            </div>
        );
    }

    return (
        <>
            <div className="rbd-banner rbd-banner--info">
                <i className="pi pi-info-circle" />
                <span>Los cambios se guardan sobre la misma cotización #{budgetId}</span>
            </div>
            {tieneExpediente(data.avisos) && (
                <div className="rbd-banner rbd-banner--warn">
                    <i className="pi pi-exclamation-triangle" />
                    <span>{COPY_EXPEDIENTE}</span>
                </div>
            )}
            <FormularioRecotizar
                budgetId={budgetId}
                data={data}
                toast={toast}
                onDone={onDone}
                onHide={onHide}
                onBloqueo={bloquear}
            />
        </>
    );
};

const RecotizarBudgetDialog = ({ budgetId, visible, onHide, onDone, toast }) => (
    <Dialog
        header={`Recotizar cotización #${budgetId ?? ''}`}
        visible={visible}
        onHide={onHide}
        modal
        maximizable
        draggable={false}
        style={{ width: '85vw', maxWidth: '1100px', borderRadius: '14px' }}
        breakpoints={{ '960px': '95vw', '641px': '100vw' }}
        headerStyle={{ borderRadius: '14px 14px 0 0' }}
        contentStyle={{ maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain', borderRadius: '0 0 14px 14px' }}
    >
        <div css={recotizarDialogCSS()} className="fk-root">
            {budgetId ? (
                <RecotizarContenido key={budgetId} budgetId={budgetId} toast={toast} onDone={onDone} onHide={onHide} />
            ) : null}
        </div>
    </Dialog>
);

export default RecotizarBudgetDialog;
